import React, { useState } from 'react';
import styled from 'styled-components';

const ExcelGenerator = () => {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState({
    title: 'New Spreadsheet',
    headers: [],
    rows: []
  });
  const [error, setError] = useState('');

  // Common business Excel patterns
  const businessPatterns = [
    {
      id: 'budget',
      name: 'Budget Tracking',
      description: 'Include columns for categories, planned amounts, actual amounts, and variance',
      active: false
    },
    {
      id: 'employees',
      name: 'Employee Records',
      description: 'Include employee ID, name, position, department, hire date, and salary',
      active: false
    },
    {
      id: 'inventory',
      name: 'Inventory Management',
      description: 'Include item name, SKU, quantity, unit price, total value, and reorder level',
      active: false
    },
    {
      id: 'sales',
      name: 'Sales Tracking',
      description: 'Include date, customer, product/service, quantity, unit price, and total amount',
      active: false
    },
    {
      id: 'expenses',
      name: 'Expense Reports',
      description: 'Include date, category, description, amount, payment method, and receipt number',
      active: false
    },
    {
      id: 'contacts',
      name: 'Contact List',
      description: 'Include name, company, email, phone, address, and notes',
      active: false
    },
    {
      id: 'projects',
      name: 'Project Timeline',
      description: 'Include task name, start date, end date, status, assigned to, and progress percentage',
      active: false
    },
    {
      id: 'revenue',
      name: 'Revenue Forecast',
      description: 'Include month, product/service, projected sales, actual sales, and variance',
      active: false
    },
    {
      id: 'performance',
      name: 'Performance Metrics',
      description: 'Include metric name, target value, actual value, percentage achieved, and status',
      active: false
    },
    {
      id: 'calendar',
      name: 'Calendar/Schedule',
      description: 'Include date, time, event/task, location, attendees, and status',
      active: false
    }
  ];

  const [patterns, setPatterns] = useState(businessPatterns);

  const togglePattern = (patternId) => {
    setPatterns(prev => 
      prev.map(pattern => 
        pattern.id === patternId 
          ? { ...pattern, active: !pattern.active }
          : pattern
      )
    );
  };

  const getActivePatterns = () => {
    return patterns.filter(pattern => pattern.active);
  };



  const generateSheet = async () => {
    if (!description.trim()) {
      setError('Please provide a description for your spreadsheet');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const activePatterns = getActivePatterns();
      
      // Use the server-side proxy for OpenAI API calls
      const apiUrl = new URL('/api/process-excel.php', window.location.origin).href;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: description,
          activePatterns: activePatterns
        })
      });

      if (!response.ok) {
        let errorMessage = `API error: ${response.status}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse JSON, just use the status text
          errorMessage = `API error: ${response.status} ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data.success || !data.result) {
        throw new Error(data.error || 'Invalid response from server');
      }

      setSheet(data.result);
    } catch (error) {
      console.error('Error generating sheet:', error);
      setError(error.message || 'Failed to generate spreadsheet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    const newRow = new Array(sheet.headers.length).fill('');
    setSheet(prev => ({
      ...prev,
      rows: [...prev.rows, newRow]
    }));
  };

  const addColumn = () => {
    const newHeader = `Column ${sheet.headers.length + 1}`;
    setSheet(prev => ({
      ...prev,
      headers: [...prev.headers, newHeader],
      rows: prev.rows.map(row => [...row, ''])
    }));
  };

  const removeRow = (rowIndex) => {
    // Don't remove if it's the last row
    if (sheet.rows.length > 1) {
      setSheet(prev => ({
        ...prev,
        rows: prev.rows.filter((_, index) => index !== rowIndex)
      }));
    }
  };

  const updateCell = (rowIndex, colIndex, value) => {
    setSheet(prev => ({
      ...prev,
      rows: prev.rows.map((row, rIdx) => 
        rIdx === rowIndex 
          ? row.map((cell, cIdx) => cIdx === colIndex ? value : cell)
          : row
      )
    }));
  };

  const updateHeader = (index, value) => {
    setSheet(prev => ({
      ...prev,
      headers: prev.headers.map((header, idx) => idx === index ? value : header)
    }));
  };

  const exportToCSV = () => {
    const csvContent = [
      sheet.headers.join(','),
      ...sheet.rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sheet.title.replace(/\s+/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const clearSheet = () => {
    setSheet({
      title: 'New Spreadsheet',
      headers: [],
      rows: []
    });
    setDescription('');
    setPatterns(businessPatterns);
  };

  return (
    <ExcelContainer>
      <Header>
        <Title>AI Excel Generator</Title>
        <ActionButtons>
          <ExportButton onClick={exportToCSV} disabled={sheet.headers.length === 0}>
            Export CSV
          </ExportButton>
          <ClearButton onClick={clearSheet}>
            Clear Sheet
          </ClearButton>
        </ActionButtons>
      </Header>

      {/* Description Input */}
      <InputSection>
        <SectionTitle>Describe Your Spreadsheet</SectionTitle>
        <DescriptionInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the Excel sheet you want to create (e.g., 'Customer order tracking with product details and payment status')"
          rows="3"
        />
        
        {/* Business Pattern Toggles */}
        <PatternSection>
          <PatternTitle>Business Templates (Optional)</PatternTitle>
          <PatternGrid>
            {patterns.map(pattern => (
              <PatternButton
                key={pattern.id}
                active={pattern.active}
                onClick={() => togglePattern(pattern.id)}
              >
                {pattern.name}
              </PatternButton>
            ))}
          </PatternGrid>
        </PatternSection>

        <GenerateButton onClick={generateSheet} disabled={loading}>
          {loading ? 'Generating...' : 'Generate Spreadsheet'}
        </GenerateButton>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </InputSection>

      {/* Spreadsheet Display */}
      {sheet.headers.length > 0 && (
        <SpreadsheetSection>
          <SpreadsheetHeader>
            <SheetTitle
              value={sheet.title}
              onChange={(e) => setSheet(prev => ({ ...prev, title: e.target.value }))}
            />
            <SheetActions>
              <AddButton onClick={addRow}>+ Add Row</AddButton>
              <AddButton onClick={addColumn}>+ Add Column</AddButton>
            </SheetActions>
          </SpreadsheetHeader>

          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <RowNumber></RowNumber>
                  {sheet.headers.map((header, index) => (
                    <HeaderCell key={index}>
                      <HeaderInput
                        value={header}
                        onChange={(e) => updateHeader(index, e.target.value)}
                      />
                    </HeaderCell>
                  ))}
                  <ActionHeaderCell>Actions</ActionHeaderCell>
                </tr>
              </thead>
              <tbody>
                {sheet.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <RowNumber>{rowIndex + 1}</RowNumber>
                    {row.map((cell, colIndex) => (
                      <TableCell key={colIndex}>
                        <CellInput
                          value={cell}
                          onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                        />
                      </TableCell>
                    ))}
                    <ActionCell>
                      <RemoveRowButton 
                        onClick={() => removeRow(rowIndex)}
                        disabled={sheet.rows.length <= 1}
                        title={sheet.rows.length <= 1 ? "Cannot remove the last row" : "Remove this row"}
                      >
                        ×
                      </RemoveRowButton>
                    </ActionCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </SpreadsheetSection>
      )}
    </ExcelContainer>
  );
};

// Styled Components
const ExcelContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
  color: #333;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const ExportButton = styled.button`
  background: #27ae60;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: #219a52;
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #c0392b;
  }
`;

const InputSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin: 0 0 1rem 0;
`;

const DescriptionInput = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid #bdc3c7;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const PatternSection = styled.div`
  margin: 2rem 0;
`;

const PatternTitle = styled.h4`
  color: #2c3e50;
  margin: 0 0 1rem 0;
`;

const PatternGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
`;

const PatternButton = styled.button`
  padding: 0.75rem;
  border: 2px solid ${props => props.active ? '#3498db' : '#bdc3c7'};
  background: ${props => props.active ? '#3498db' : 'white'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover {
    border-color: #3498db;
    background: ${props => props.active ? '#2980b9' : '#ecf0f1'};
  }
`;

const GenerateButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  width: 100%;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: #2980b9;
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #e74c3c;
  color: white;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`;

const SpreadsheetSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const SpreadsheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: #34495e;
  color: white;
`;

const SheetTitle = styled.input`
  background: transparent;
  border: none;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  
  &:focus {
    outline: 1px solid white;
    border-radius: 2px;
  }
`;

const SheetActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const AddButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #2980b9;
  }
`;

const TableContainer = styled.div`
  overflow: auto;
  max-height: 600px;
  border: 1px solid #bdc3c7;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
`;

const RowNumber = styled.td`
  background: #ecf0f1;
  padding: 0.5rem;
  text-align: center;
  font-weight: 600;
  color: #7f8c8d;
  border-right: 1px solid #bdc3c7;
  border-bottom: 1px solid #bdc3c7;
  width: 50px;
  position: sticky;
  left: 0;
  z-index: 1;
`;

const HeaderCell = styled.th`
  background: #34495e;
  border-bottom: 1px solid #bdc3c7;
  border-right: 1px solid #bdc3c7;
  padding: 0;
  min-width: 120px;
`;

const ActionHeaderCell = styled.th`
  background: #34495e;
  border-bottom: 1px solid #bdc3c7;
  border-right: 1px solid #bdc3c7;
  padding: 0.75rem;
  width: 80px;
  color: white;
  font-weight: 600;
  text-align: center;
  font-size: 0.9rem;
`;

const HeaderInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: none;
  background: transparent;
  color: white;
  font-weight: 600;
  text-align: center;
  
  &:focus {
    outline: 2px solid #3498db;
    background: rgba(52, 73, 94, 0.8);
  }
`;

const TableCell = styled.td`
  border-right: 1px solid #bdc3c7;
  border-bottom: 1px solid #bdc3c7;
  padding: 0;
  min-width: 120px;
`;

const ActionCell = styled.td`
  border-right: 1px solid #bdc3c7;
  border-bottom: 1px solid #bdc3c7;
  padding: 0.5rem;
  width: 80px;
  text-align: center;
  background: #f8f9fa;
`;

const RemoveRowButton = styled.button`
  background: ${props => props.disabled ? '#bdc3c7' : '#e74c3c'};
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #c0392b;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const CellInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  background: transparent;
  
  &:focus {
    outline: 2px solid #3498db;
    background: #ecf0f1;
  }
`;

export default ExcelGenerator; 